import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

interface EmergencyDashboardItem {
  emergencia_id: number;
  dashboard?: string | null;
  dasbhoard?: string | null;
}

const cleanDashboardUrl = (url?: string | null) => String(url || '').trim().replace(/^['"]+|['"]+$/g, '');

const toEmbeddableDashboardUrl = (url: string) =>
  url
    .replace(/^(https?:\/\/[^/]+)\/reporting\//, (_match, origin) => `${origin}/embed/reporting/`)
    .replace(/^(https?:\/\/[^/]+)\/embed\/embed\/reporting\//, (_match, origin) => `${origin}/embed/reporting/`);

const getStoredDashboardUrl = (selectedEmergenciaId: number | null) => {
  const storedId = Number(localStorage.getItem('selectedDashboardEmergenciaId') || 'NaN');
  if (!selectedEmergenciaId || storedId !== selectedEmergenciaId) return '';
  return cleanDashboardUrl(localStorage.getItem('selectedDashboardUrl'));
};

export const Dashboard: React.FC = () => {
  const { authFetch, datosLogin, loginResponse, selectedEmergenciaId } = useAuth();
  const apiBase = process.env.REACT_APP_API_URL || '/api';
  const [dashboardReportUrl, setDashboardReportUrl] = useState(() => getStoredDashboardUrl(selectedEmergenciaId));
  const iframeUrl = toEmbeddableDashboardUrl(dashboardReportUrl);

  useEffect(() => {
    const loadDashboardUrl = async () => {
      const selectedId = Number(selectedEmergenciaId ?? localStorage.getItem('selectedEmergenciaId') ?? 0);
      const username = datosLogin?.usuario_login || loginResponse?.usuario || '';

      if (!selectedId || !username) {
        setDashboardReportUrl('');
        return;
      }

      const storedUrl = getStoredDashboardUrl(selectedId);
      if (storedUrl) {
        setDashboardReportUrl(storedUrl);
      }

      try {
        const res = await authFetch(`${apiBase}/emergencias/usuario/${encodeURIComponent(username)}`, {
          headers: { accept: 'application/json' },
        });
        if (!res.ok) {
          if (!storedUrl) setDashboardReportUrl('');
          return;
        }

        const data = await res.json();
        const items = Array.isArray(data) ? (data as EmergencyDashboardItem[]) : [];
        const selectedItem = items.find((item) => Number(item.emergencia_id) === selectedId);
        const dashboardUrl = cleanDashboardUrl(selectedItem?.dashboard || selectedItem?.dasbhoard);

        setDashboardReportUrl(dashboardUrl);
        if (dashboardUrl) {
          localStorage.setItem('selectedDashboardUrl', dashboardUrl);
          localStorage.setItem('selectedDashboardEmergenciaId', String(selectedId));
        } else {
          localStorage.removeItem('selectedDashboardUrl');
          localStorage.removeItem('selectedDashboardEmergenciaId');
        }
      } catch {
        if (!storedUrl) setDashboardReportUrl('');
      }
    };

    loadDashboardUrl();
  }, [apiBase, authFetch, datosLogin?.usuario_login, loginResponse?.usuario, selectedEmergenciaId]);

  if (!dashboardReportUrl) {
    return (
      <div className="container-fluid d-flex align-items-center justify-content-center" style={{ height: '100vh' }}>
        <div className="text-muted">No hay dashboard configurado para la emergencia seleccionada.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <iframe
        src={iframeUrl}
        width="100%"
        height="100%"
        frameBorder="0"
        style={{ border: 0 }}
        allowFullScreen
      />
    </div>
  );
};
